import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Building2, Mail, Phone, MapPin, CalendarCheck, Users, Briefcase, IndianRupee, ShieldCheck, ShieldAlert, Inbox } from 'lucide-react';
import { fmtDate } from './AcademyDrilldown';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-border/60 last:border-0 text-sm">
    <span className="inline-flex items-center gap-2 text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</span>
    <span className="font-medium text-right break-all" data-testid={testId}>{value || '—'}</span>
  </div>
);

const Stat = ({ label, value, sub, testId }) => (
  <Card><CardContent className="p-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-2xl font-display font-bold tabular-nums mt-1" data-testid={testId}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </CardContent></Card>
);

const statusCls = (s) => ((s || 'active').toLowerCase() === 'active'
  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
  : (s || '').toLowerCase() === 'paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
  : (s || '').toLowerCase() === 'overdue' ? 'bg-red-100 text-red-700 border-red-200'
  : 'bg-slate-100 text-slate-600 border-slate-200');

export default function AdminPersonProfile() {
  const { academyId, kind, personId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    api.get(`/admin/academies/${academyId}/people/${kind}/${personId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load this profile'));
  }, [academyId, kind, personId]);

  const isStudent = kind === 'students';
  const backLink = (
    <Button asChild variant="ghost" size="sm" className="h-8 px-2 -ml-2" data-testid="person-back-link">
      <Link to="/super-admin/academies"><ArrowLeft className="h-4 w-4 mr-1" /> Academies</Link>
    </Button>
  );

  if (error) return (
    <div className="space-y-4" data-testid="person-profile-error">{backLink}
      <Card><CardContent className="p-10 text-center">
        <ShieldAlert className="h-8 w-8 mx-auto mb-2 text-red-400" />
        <p className="text-sm font-medium">{error}</p>
      </CardContent></Card>
    </div>
  );
  if (!data) return (
    <div className="space-y-4" data-testid="person-profile-loading">{backLink}
      <Skeleton className="h-28 rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  const { person: p, academy, account, attendance, fees = [], fee_summary: fs, payslips = [] } = data;

  return (
    <div className="space-y-6" data-testid="person-profile-page">
      {backLink}

      <Card className="overflow-hidden">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className={`h-16 w-16 rounded-2xl flex items-center justify-center text-xl font-display font-bold text-white shrink-0 ${isStudent ? 'bg-emerald-600' : 'bg-blue-600'}`}>
            {initials(p.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-display font-semibold truncate" data-testid="person-name">{p.name}</h1>
              <Badge variant="outline" className="font-mono text-xs" data-testid="person-custom-id">{p.custom_id || '—'}</Badge>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${statusCls(p.status)}`} data-testid="person-status">{p.status || 'active'}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1.5 flex-wrap">
              {isStudent ? <Users className="h-3.5 w-3.5" /> : <Briefcase className="h-3.5 w-3.5" />}
              {isStudent ? `Student · ${p.sport || 'General'}${p.batch ? ` · ${p.batch} batch` : ''}` : `${p.designation || p.role || 'Employee'}${p.department ? ` · ${p.department}` : ''}`}
              <span className="text-muted-foreground/60">·</span>
              <Building2 className="h-3.5 w-3.5" /> <span data-testid="person-academy">{academy.name}</span>
              {academy.custom_id && <span className="font-mono text-xs">({academy.custom_id})</span>}
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground shrink-0">
            <p>Joined</p>
            <p className="text-sm font-medium text-foreground tabular-nums" data-testid="person-joined">{fmtDate(p.join_date || p.created_at)}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Attendance · 30 days" value={attendance.pct === null ? '—' : `${attendance.pct}%`} sub={`${attendance.last_30_present}/${attendance.last_30_total} sessions`} testId="person-attendance" />
        {isStudent ? (
          <>
            <Stat label="Fees Paid" value={inr(fs.paid_total)} sub={`${fs.paid_count} receipts`} testId="person-fees-paid" />
            <Stat label="Pending" value={inr(fs.pending_amount)} sub={`${fs.pending_count} invoice${fs.pending_count === 1 ? '' : 's'}`} testId="person-fees-pending" />
            <Stat label="Last Payment" value={fs.last_payment_date ? fmtDate(fs.last_payment_date) : '—'} testId="person-last-payment" />
          </>
        ) : (
          <>
            <Stat label="Payslips" value={payslips.length} sub="recent" testId="person-payslips" />
            <Stat label="Role" value={p.role || '—'} testId="person-role" />
            <Stat label="Department" value={p.department || '—'} testId="person-department" />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Contact & Details</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <Row icon={Phone} label="Phone" value={p.phone} testId="person-phone" />
            <Row icon={Mail} label="Email" value={p.email} />
            {isStudent && <Row icon={Users} label="Guardian" value={[p.guardian_name, p.guardian_phone].filter(Boolean).join(' · ')} testId="person-guardian" />}
            {isStudent && <Row icon={CalendarCheck} label="Date of Birth" value={p.dob ? fmtDate(p.dob) : null} />}
            {isStudent && <Row icon={ShieldCheck} label="Blood Group" value={p.blood_group} />}
            {isStudent && <Row icon={Phone} label="Emergency Contact" value={p.emergency_contact} />}
            <Row icon={MapPin} label="Address" value={p.address} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Portal Account</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {account ? (
              <>
                <Row icon={Mail} label="Login email" value={account.email} testId="person-account-email" />
                <Row icon={ShieldCheck} label="Verified" value={account.is_verified ? 'Yes' : 'No'} />
                <Row icon={CalendarCheck} label="Last login" value={account.last_login_at ? fmtDate(account.last_login_at) : 'Never'} />
                <Row icon={ShieldAlert} label="Must reset password" value={account.password_must_reset ? 'Yes' : 'No'} />
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="person-no-account">No portal login created yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {isStudent && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm inline-flex items-center gap-2"><IndianRupee className="h-4 w-4 text-emerald-600" /> Fee History</CardTitle>
            <Badge variant="secondary" className="text-xs tabular-nums">{fees.length}</Badge>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs">Receipt</TableHead><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs">Period</TableHead>
                <TableHead className="text-xs">Due</TableHead><TableHead className="text-xs">Paid On</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs text-right">Amount</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {fees.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10"><Inbox className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" /><p className="text-sm text-muted-foreground">No fees recorded</p></TableCell></TableRow>
                ) : fees.map((f, i) => (
                  <TableRow key={f.id || i} data-testid={`person-fee-row-${i}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{f.custom_id || '—'}</TableCell>
                    <TableCell className="text-sm capitalize">{f.fee_type || '—'}</TableCell>
                    <TableCell className="text-sm">{f.period || '—'}</TableCell>
                    <TableCell className="text-sm tabular-nums">{fmtDate(f.due_date)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{f.payment_date ? fmtDate(f.payment_date) : '—'}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${statusCls(f.status)}`}>{f.status || '—'}</span></TableCell>
                    <TableCell className="text-sm text-right font-semibold tabular-nums">{inr(f.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
